import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SearchFilters } from '../types';

interface SearchBarProps {
  onSearch: (filters: SearchFilters) => void;
  loading?: boolean;
  placeholder?: string;
}

interface SearchSuggestion {
  type: 'protein' | 'pdb' | 'organism';
  value: string;
  label: string;
  count?: number;
}

interface SmartNoMatchesProps {
  query: string;
  isMobile: boolean;
  onSuggestionClick: (suggestion: SearchSuggestion) => void;
}

const SmartNoMatchesMessage: React.FC<SmartNoMatchesProps> = ({ query, isMobile, onSuggestionClick }) => {
  const [similarSuggestions, setSimilarSuggestions] = useState<SearchSuggestion[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSimilarEntries = async () => {
      const cleanQuery = query.trim();
      if (cleanQuery.length < 3) return;

      setLoading(true);
      try {
        // Try fuzzy search for similar entries
        const backendUrl = '';
        
        // Create partial search queries
        const searchQueries = [];
        
        // For potential protein names, try partial matches
        if (cleanQuery.length >= 4) {
          searchQueries.push(cleanQuery.substring(0, Math.floor(cleanQuery.length * 0.75)));
          searchQueries.push(cleanQuery.substring(0, cleanQuery.length - 1));
          searchQueries.push(cleanQuery.substring(0, cleanQuery.length - 2));
        }
        
        for (const partialQuery of searchQueries) {
          if (partialQuery.length >= 3) {
            try {
              const response = await fetch(`${backendUrl}/search/suggestions?query=${encodeURIComponent(partialQuery)}&limit=3`);
              if (response.ok) {
                const data = await response.json();
                if (data.suggestions && data.suggestions.length > 0) {
                  // Filter to show only the most relevant ones
                  const relevant = data.suggestions.filter((s: SearchSuggestion) => 
                    s.value.toLowerCase().includes(partialQuery.toLowerCase()) ||
                    s.label.toLowerCase().includes(partialQuery.toLowerCase())
                  );
                  if (relevant.length > 0) {
                    setSimilarSuggestions(relevant.slice(0, 3));
                    break;
                  }
                }
              }
            } catch (e) {
              continue;
            }
          }
        }
      } catch (error) {
        console.warn('Failed to fetch similar entries:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSimilarEntries();
  }, [query]);

  const getContextMessage = () => {
    const cleanQuery = query.trim();
    if (cleanQuery.match(/^\d[A-Z0-9]{3}$/i)) {
      return `The PDB ID "${cleanQuery}" hasn't been curated for exosite information.`;
    } else if (cleanQuery.match(/^[A-Z]\d+[A-Z]?$/i)) {
      return `The UniProt ID "${cleanQuery}" is not in our database yet. Try searching by protein name instead.`;
    } else if (cleanQuery.match(/^[A-Z]{2,}[\d\-_]*$/i)) {
      return `"${cleanQuery}" is not in our current entries. If this is a protein abbreviation, try the full name or check spelling.`;
    } else {
      return `"${cleanQuery}" is not found. Try searching with protein names, PDB IDs (e.g., 1ABC), or UniProt IDs (e.g., P12345).`;
    }
  };

  return (
    <div style={{
      padding: isMobile ? '12px' : '16px',
      color: '#64748b',
      backgroundColor: '#f8fafc',
      borderRadius: '6px',
      margin: isMobile ? '4px' : '8px'
    }}>
      <div style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '8px', textAlign: 'center' }}>🔍</div>
      
      <div style={{ 
        fontSize: isMobile ? '12px' : '14px', 
        fontWeight: '600', 
        marginBottom: '8px',
        textAlign: 'center',
        color: '#374151'
      }}>
        "{query}" not found in current entries
      </div>
      
      {similarSuggestions.length > 0 ? (
        <div>
          <div style={{
            fontSize: isMobile ? '11px' : '12px',
            marginBottom: '8px',
            color: '#3b82f6',
            textAlign: 'center',
            fontWeight: '600'
          }}>
            Did you mean one of these?
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {similarSuggestions.map((suggestion, index) => (
              <button
                key={`similar-${index}`}
                onClick={() => onSuggestionClick(suggestion)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#e0f2fe',
                  border: '1px solid #0ea5e9',
                  borderRadius: '4px',
                  fontSize: isMobile ? '11px' : '12px',
                  color: '#0c4a6e',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#bae6fd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#e0f2fe';
                }}
              >
                <span style={{
                  fontSize: '10px',
                  backgroundColor: suggestion.type === 'protein' ? '#dbeafe' : 
                                suggestion.type === 'pdb' ? '#dcfce7' : '#fce7f3',
                  color: suggestion.type === 'protein' ? '#1d4ed8' :
                         suggestion.type === 'pdb' ? '#166534' : '#be185d',
                  padding: '1px 4px',
                  borderRadius: '2px',
                  marginRight: '6px'
                }}>
                  {suggestion.type.toUpperCase()}
                </span>
                {suggestion.value}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div style={{
          fontSize: isMobile ? '11px' : '12px',
          lineHeight: '1.4',
          color: '#64748b',
          textAlign: 'center'
        }}>
          {loading ? 'Looking for similar entries...' : getContextMessage()}
        </div>
      )}
    </div>
  );
};

// Simple SVG icons
const MagnifyingGlassIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);

const XMarkIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SearchBar: React.FC<SearchBarProps> = ({ 
  onSearch, 
  loading = false, 
  placeholder = "Search proteins, PDB IDs, organisms..." 
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isSmallMobile = windowWidth < 480;

  // Fetch suggestions from API
  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    try {
      const backendUrl = '';
      const response = await fetch(`${backendUrl}/search/suggestions?query=${encodeURIComponent(query)}&limit=8`);
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.warn('Failed to fetch suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce timer ref
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedSuggestion(-1);
    
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Debounce API calls
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestion(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestion(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestion >= 0) {
          selectSuggestion(suggestions[selectedSuggestion]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestion(-1);
        break;
    }
  };

  const selectSuggestion = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.value);
    setShowSuggestions(false);
    setSelectedSuggestion(-1);
    
    // Create search filters based on suggestion type
    const filters: SearchFilters = {};
    switch (suggestion.type) {
      case 'protein':
        filters.protein_name = suggestion.value;
        break;
      case 'pdb':
        filters.pdb_id = suggestion.value;
        break;
      case 'organism':
        filters.organism = suggestion.value;
        break;
    }
    
    onSearch(filters);
  };

  const handleSearch = () => {
    if (query.trim()) {
      // Smart search - try to detect what type of query it is
      const filters: SearchFilters = {};
      const cleanQuery = query.trim();
      
      // Enhanced pattern detection
      if (cleanQuery.match(/^\d[A-Z0-9]{3}$/i)) {
        // Looks like PDB ID (e.g., 1CM8, 2ABC)
        filters.pdb_id = cleanQuery.toUpperCase();
        filters._searchType = 'pdb_id';
        filters._originalQuery = cleanQuery;
      } else if (cleanQuery.match(/^[A-Z]\d+[A-Z]?$/i)) {
        // Looks like UniProt ID (e.g., P12345, Q9Y6R7)
        filters.uniprot_id = cleanQuery.toUpperCase();
        filters._searchType = 'uniprot_id';
        filters._originalQuery = cleanQuery;
      } else if (cleanQuery.match(/^[A-Z]{2,}[\d\-_]*$/i)) {
        // Looks like protein abbreviation (e.g., TP53, EGFR, BRCA1)
        filters.protein_name = cleanQuery;
        filters._searchType = 'protein_abbreviation';
        filters._originalQuery = cleanQuery;
      } else {
        // General protein name search
        filters.protein_name = cleanQuery;
        filters._searchType = 'protein_name';
        filters._originalQuery = cleanQuery;
      }
      
      onSearch(filters);
    }
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    setQuery('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <div style={{ 
      position: 'relative', 
      maxWidth: isMobile ? '100%' : '512px', 
      margin: '0 auto',
      width: '100%',
      padding: isMobile ? '0 16px' : '0'
    }}>
      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          paddingLeft: isMobile ? '10px' : '12px',
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none'
        }}>
          <MagnifyingGlassIcon 
            style={{ 
              height: isMobile ? '18px' : '20px', 
              width: isMobile ? '18px' : '20px', 
              color: (loading || loadingSuggestions) ? '#3b82f6' : '#9ca3af',
              animation: loadingSuggestions ? 'pulse 1.5s ease-in-out infinite' : 'none'
            }} 
          />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={isMobile ? 'Search proteins...' : placeholder}
          disabled={loading}
          style={{
            width: '100%',
            paddingLeft: isMobile ? '35px' : '40px',
            paddingRight: isMobile ? '35px' : '40px',
            fontSize: isMobile ? '16px' : '18px',
            height: isMobile ? '44px' : '48px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            transition: 'box-shadow 0.2s',
            border: '1px solid #cbd5e1',
            borderRadius: isMobile ? '8px' : '6px',
            outline: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'textfield'
          }}
          onFocus={(e) => {
            e.target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
            e.target.style.borderColor = '#3b82f6';
            if (query.length > 1) setShowSuggestions(suggestions.length > 0);
          }}
          onBlur={(e) => {
            e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
            e.target.style.borderColor = '#cbd5e1';
          }}
        />
        
        {query && (
          <button
            onClick={clearSearch}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              paddingRight: isMobile ? '10px' : '12px',
              display: 'flex',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#64748b'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
          >
            <XMarkIcon style={{ height: isMobile ? '18px' : '20px', width: isMobile ? '18px' : '20px' }} />
          </button>
        )}
      </div>

      {/* Search Suggestions - Mobile Responsive */}
      {showSuggestions && (
        <div 
          ref={suggestionsRef}
          style={{
            position: 'absolute',
            zIndex: 50,
            width: '100%',
            marginTop: isMobile ? '4px' : '8px',
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: isMobile ? '6px' : '8px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxHeight: isMobile ? '250px' : '384px',
            overflowY: 'auto'
          }}
        >
          {suggestions.length > 0 ? (
            suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.type}-${suggestion.value}`}
                onClick={() => selectSuggestion(suggestion)}
                style={{
                  width: '100%',
                  padding: isMobile ? '10px 12px' : '12px 16px',
                  textAlign: 'left',
                  backgroundColor: index === selectedSuggestion ? '#eff6ff' : 'transparent',
                  color: index === selectedSuggestion ? '#1d4ed8' : '#374151',
                  border: 'none',
                  borderBottom: index < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  fontSize: isMobile ? '14px' : '16px'
                }}
                onMouseEnter={(e) => {
                  if (index !== selectedSuggestion) {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (index !== selectedSuggestion) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: isMobile ? '6px' : '12px',
                  flexWrap: 'nowrap'
                }}>
                  <span style={{
                    padding: isMobile ? '2px 6px' : '2px 8px',
                    fontSize: isMobile ? '9px' : '12px',
                    fontWeight: '500',
                    borderRadius: '9999px',
                    backgroundColor: suggestion.type === 'protein' ? '#dbeafe' : 
                                    suggestion.type === 'pdb' ? '#dcfce7' : '#fce7f3',
                    color: suggestion.type === 'protein' ? '#1d4ed8' :
                           suggestion.type === 'pdb' ? '#166534' : '#be185d',
                    flexShrink: 0
                  }}>
                    {suggestion.type.toUpperCase()}
                  </span>
                  <span style={{ 
                    fontWeight: '500',
                    fontSize: isMobile ? '14px' : '16px',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {suggestion.value}
                  </span>
                  {suggestion.label !== suggestion.value && !isSmallMobile && (
                    <span style={{ 
                      color: '#6b7280', 
                      fontSize: isMobile ? '12px' : '14px',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {suggestion.label.replace(suggestion.value + ' - ', '')}
                    </span>
                  )}
                </div>
              </button>
            ))
          ) : (
            /* Smart no matches message with similar suggestions */
            <SmartNoMatchesMessage 
              query={query} 
              isMobile={isMobile} 
              onSuggestionClick={selectSuggestion}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;